import { useState, useEffect } from 'react';
import { goalsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Target, Pencil, X, Check, TrendingUp, Calendar } from 'lucide-react';

const YEAR = new Date().getFullYear();

export default function ReadingGoalWidget({ username, isOwn = false }) {
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    goalsApi.getGoal(username, YEAR)
      .then(setGoal)
      .catch(() => setGoal(null))
      .finally(() => setLoading(false));
  }, [username]);

  const handleSave = async () => {
    const target = parseInt(inputVal, 10);
    if (!target || target < 1 || target > 999) return;
    setSaving(true);
    try {
      const saved = await goalsApi.setGoal(YEAR, target);
      // Re-fetch to get progress too
      const updated = await goalsApi.getGoal(username, YEAR);
      setGoal(updated);
      setEditing(false);
    } catch (_) {
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await goalsApi.deleteGoal(YEAR);
    setGoal(null);
    setEditing(false);
  };

  if (loading) return null;

  // No goal set and not own profile — hide
  if (!goal && !isOwn) return null;

  // No goal set, own profile — show prompt
  if (!goal && isOwn) {
    if (editing) {
      return (
        <GoalEditor
          inputVal={inputVal}
          setInputVal={setInputVal}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          saving={saving}
        />
      );
    }
    return (
      <button
        onClick={() => { setEditing(true); setInputVal(''); }}
        className="w-full flex items-center gap-2 px-4 py-3 border border-dashed border-cream-dark rounded-xl text-ink-muted hover:border-terra hover:text-terra transition-colors text-sm"
      >
        <Target className="w-4 h-4 flex-shrink-0" />
        Set a {YEAR} reading goal
      </button>
    );
  }

  const pct = Math.min(100, Math.round((goal.progress / goal.target) * 100));
  const done = goal.progress >= goal.target;

  return (
    <div className="bg-cream border border-cream-dark rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-terra" />
          <span className="text-sm font-semibold text-ink">{YEAR} Reading Goal</span>
        </div>
        {isOwn && !editing && (
          <button
            onClick={() => { setEditing(true); setInputVal(String(goal.target)); }}
            className="text-ink-muted hover:text-terra transition-colors"
            aria-label="Edit goal"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {editing ? (
        <GoalEditor
          inputVal={inputVal}
          setInputVal={setInputVal}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          onDelete={handleDelete}
          saving={saving}
        />
      ) : (
        <>
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-heading text-2xl text-ink">{goal.progress}</span>
            <span className="text-ink-muted text-sm">/ {goal.target} books</span>
            {done && <span className="text-xs font-semibold text-terra ml-1">🎉 Goal reached!</span>}
          </div>
          <div className="w-full bg-cream-dark rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${done ? 'bg-forest' : 'bg-terra'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-xs text-ink-muted">{pct}% complete</p>
            {goal && (
              <button
                onClick={async () => {
                  setShowHistory(true);
                  setLoadingHistory(true);
                  try {
                    const data = await goalsApi.getHistoricalProgress(username);
                    setHistoricalData(data);
                  } catch (_) {
                    setHistoricalData([]);
                  } finally {
                    setLoadingHistory(false);
                  }
                }}
                className="text-xs text-terra hover:text-terra/80 transition-colors flex items-center gap-1"
              >
                <TrendingUp className="w-3 h-3" />
                Past years
              </button>
            )}
          </div>
        </>
      )}

      {/* Historical Progress Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-cream border-cream-dark max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-ink flex items-center gap-2">
              <Calendar className="w-5 h-5 text-terra" />
              Reading History
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingHistory ? (
              <div className="text-center py-8 text-ink-muted text-sm">Loading...</div>
            ) : historicalData.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-ink-muted text-sm">No historical data available yet.</p>
                <p className="text-xs text-ink-muted mt-1">Import books from Goodreads to see your reading history.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-ink-muted mb-3">
                  Based on your current goal of {goal?.target} books/year
                </p>
                {historicalData.map((yearData) => (
                  <div key={yearData.year} className="bg-cream-dark rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-ink">{yearData.year}</span>
                      <span className="text-sm text-ink-muted">
                        {yearData.progress} / {yearData.target} books
                      </span>
                    </div>
                    <div className="w-full bg-cream rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          yearData.percentage >= 100 ? 'bg-forest' : 'bg-terra'
                        }`}
                        style={{ width: `${Math.min(100, yearData.percentage)}%` }}
                      />
                    </div>
                    <p className="text-xs text-ink-muted mt-1">
                      {yearData.percentage}% {yearData.percentage >= 100 ? '🎉' : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GoalEditor({ inputVal, setInputVal, onSave, onCancel, onDelete, saving }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-muted">How many books do you want to read in {YEAR}?</p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="1"
          max="999"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder="e.g. 24"
          className="bg-cream-dark border-cream-dark focus:border-terra text-ink w-24 h-8 text-sm"
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
          autoFocus
        />
        <Button
          size="sm"
          onClick={onSave}
          disabled={saving || !inputVal || parseInt(inputVal) < 1}
          className="bg-terra hover:bg-terra/90 text-cream h-8 px-3"
        >
          <Check className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          className="text-ink-muted h-8 px-2"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
      {onDelete && (
        <button
          onClick={onDelete}
          className="text-xs text-ink-muted hover:text-red-500 transition-colors"
        >
          Remove goal
        </button>
      )}
    </div>
  );
}
